import Vue from 'vue';
import $ from 'jquery';
import {Button, FloatingActionButton} from '@progress/kendo-vue-buttons';
import clone from 'lodash.clonedeep';

import Grid from './grid';
import Hotkeys from './hotkeys';
import Menu from './menu';

import Audit from '../components/audit.vue';
import Demographics from '../components/demographics.vue';
import DrawPad from '../components/drawpad.vue';
import Narrative from '../components/narrative.vue';
import SearchBox from '../components/searchbox.vue';
import SigPad from '../components/sigpad.vue';
import SimpleDemos from '../components/simpledemos.vue';
import Style from '../components/style.js';

const BAD_MENU_TARGETS = '.k-animation-container, .k-icon, .k-menu-item, .k-menu-link, .k-window-title';
const INJECT = [
  'constants', 'on_calendar_change', 'on_context_menu_open', 'on_grid_bound',
  'on_grid_navigate', 'on_grid_select', 'on_menu_select', 'on_tab_activate',
  'on_tab_contentload', 'on_tab_select', 'td_templates', 'tr_compare',
  'tr_compare_date', 'tr_compare_icon', 'tr_compare_numeric', 'tr_compare_time'
];


function chain_callbacks(buffer_entry) {
  setImmediate(function () {
    if (buffer_entry.$callbacks && buffer_entry.$callbacks.length) {
      buffer_entry.$callbacks.shift().call(buffer_entry);
      chain_callbacks(buffer_entry);
    } else if (buffer_entry.$finally) {
      buffer_entry.$finally();
    } else {
      buffer_entry.$callbacks = [];
    }
  });
}

function get_watcher(self, role) {
  return {
    handler: function (file) {
      this.$root.$options.socket.emit('register', {
        id: self.getAttribute('id') || self.getAttribute('ref'), type: role,
        file: file
      });
    },
    immediate: true
  };
}

function post_load(apply_focus) {
  console.debug('post-load', this, this.$el);
  for (var key in this.components) {
    var component = this.components[key];
    var id = component && component.id;
    if (!id)
      continue;
    switch (component.role) {
    case 'button':
    case 'floatingbutton':
    case 'checkbox':
      if (component.hotkey)
        this.hotkeys.add(component.hotkey, {id: id});
      break;
    case 'tabstrip':
      component.datasource.forEach(function (tab, index) {
        if (tab.hotkey)
          this.hotkeys.add(tab.hotkey, {id: id, index: index});
      }, this);
      $('#' + id + '>.k-tabstrip-items').addClass('k-tabstrip-items-wrapper');
      break;
    default:
    }
  }
  if (apply_focus)
    this.$root.apply_default_focus($(this.$el));
}

function window_actions_click(win, event) {
  console.debug('on_action_click', this, win, event);
  var target = event.target;
  var button = $(target).closest('a');
  var related = event.relatedTarget;
  if (
    (related && !related.isConnected) || win.hasClass('background') ||
    button.hasClass('chi-menu-open')
  ) {
    event.stopImmediatePropagation();
    return event.preventDefault();
  }
  var ds_key = 'top_menu_data';
  var label = button.attr('aria-label');
  switch (label) {
  case 'close':
    if (event.type === 'click')
      this.$root.$options.socket.emit('close', {});
    break;
  case 'jump-out':
    if (event.type === 'click')
      this.$root.$options.socket.emit('click', {
        id: 'context-menu', value: 'returntomm'
      });
    break;
  case 'context-menu':
    ds_key = 'context_menu_data';
    // falls through
  case 'hamburger-menu':
    if (
      event.type === 'click' ||
      (target.tagName === 'A' && !$(related).is(BAD_MENU_TARGETS))
    )
      Menu
        .create_menu(this, button, this.$root[ds_key], label)
        .data('kendo-context-menu')
        .open();
    break;
  }
}

// wrap window events so that we can disable them when the window is
// superseded by another
function wrap_window_events(win, elements) {
  for (var i = elements.length - 1; i >= 0; --i) {
    var $target = elements[i];
    var events = $._data($target.get(0), 'events') || {};
    var keys = Object.keys(events);
    for (var j = keys.length - 1; j >= 0; --j) {
      var these_events = events[keys[j]];
      for (var k = these_events.length - 1; k >= 0; --k) {
        var listener = these_events[k];
        var handler = listener.handler;
        listener.handler = function () {
          if (!win.hasClass('background'))
            handler.apply(this, Array.from(arguments));
        };
      }
    }
  }
}

function add_element(config, in_place) {
  var vm = config.parent;
  var set = in_place ?
    function (object_key, prop_key, value) {
      Vue.set(vm.$data[object_key], prop_key, value);
    } :
    function (object_key, prop_key, value) {
      config.data[object_key][prop_key] = value;
    };
  return function () {
    if (!this.getAttribute('data-properties'))
      console.info('no properties', this);
    var $self = $(this);
    var id = this.getAttribute('id') || this.getAttribute('ref');
    var safe_id = $self.data('safe-id');
    // merge the various sources of properties into one
    var props = eval(
      '(function (' + Object.keys(Grid).join(', ') + ') {' +
        'return (' + this.getAttribute('data-properties') + ');' +
      '})'
    )(...Object.keys(Grid).map(function (key) {return Grid[key]}));
    var buffer = vm.$root.get_props(safe_id);
    var shared = (props.shared && vm.$root.shared[safe_id]) || {};
    console.debug('adding %s:', safe_id, props, shared, buffer);
    // merge props by priority (buffer is most important, props least)
    Object.assign(props, shared, buffer);
    // copy props into buffer and make shared and props a reference to it,
    // because commands might be holding references to the buffer
    props = Object.assign(buffer, props);
    if (props.shared)
      vm.$root.shared[safe_id] = buffer;
    // update props as needed
    props.id = id;
    props.role = $self.attr('role');
    switch (props.role) {
    case 'calendar':
    case 'datepicker':
      props.value =
        isNaN(Date.parse(props.value)) ? null : new Date(props.value);
      break;
    case 'grid':
      if (props.columns)
        props.columns.forEach(function (col) {
          if (typeof col.width === 'number')
            col.width = Math.round(100 * col.width / props.width, 2) + '%';
        });
      break;
    case 'htmlarea':
    case 'narrative':
      props.style = props.style || {};
      props.style.position = 'relative';
      break;
    case 'label':
    case 'textarea':
    case 'textfield':
      if ('maxlength' in props) {
        props.maxlength = +props.maxlength;
        $self.on('input', function () {
          if (self.value.length === props.maxlength)
            vm.$root.next_field(self);
        });
        $self.data('oninput-set', true)
      } else {
        props.maxlength = -1;
      }
      break;
    case 'maskedtextbox':
      // give kendo time to replace the kendo-maskedtextbox element (which
      // is what self and $self reference) with the actual input
      setImmediate(function () {
        var element = $('#' + id);
        element.data('prev-last-char', '_');
      });
      break;
    case 'tabstrip':
      $self.attr('data-tab-load-ts', Date.now());
      break;
    }
    set('components', safe_id, props);
    if ('file' in props) {
      var watcher = get_watcher(this, props.role);
      if (in_place)
        vm.$watch('components.' + safe_id + '.file', watcher);
      else
        config.watch['components.' + safe_id + '.file'] = watcher;
      props.$destroy = function () {
        vm.$root.$options.socket.emit('unregister', {id: id});
      };
    }
    if (props.shared && !props.orig_props)
      props.orig_props = clone(props);
    chain_callbacks(props);
  };
}

function activate_content(element, config, in_place) {
  console.debug('activate-content', this, element, config, in_place);
  element.removeClass('xxxs xxs xs sm md lg xl full-screen');
  element.find('input[readonly]').attr('tabindex', '-1');
  element.on('keydown', function (event) {
    if (event.which === 27) {
      event.preventDefault();
      event.stopImmediatePropogation();
    }
  });
  config.components = {
    'chi-audit': Audit,
    'chi-demographics': Demographics,
    'chi-drawpad': DrawPad,
    'chi-narrative': Narrative,
    'chi-searchbox': SearchBox,
    'chi-sigpad': SigPad,
    'chi-simple-demos': SimpleDemos,
    'chi-style': Style,
    'kendo-button': Button,
    'kendo-floating-action-button': FloatingActionButton
  };
  config.el = element.get(0);
  if (config.inject)
    config.inject.splice(0, 0, ...INJECT);
  else
    config.inject = INJECT;
  if (element.find('[role="tabstrip"]').length > 0)
    element.addClass('with-tabs');
  element.find('[data-safe-id]').each(add_element(config, in_place));
  console.debug('activate:', config);
  console.debug('vue:', new Vue(config));
}

function on_tab_contentload(event) {
  console.debug('activate-tab-content', this, event);
  var parent_id = event.sender.element.attr('id');
  var vm = this.$root.$options.api.vm;
  if (!(parent_id in vm.$refs) && parent_id in vm.$parent.$refs)
    vm = vm.$parent;
  var tab_content = $(event.contentElement);
  activate_content.call(this, tab_content.find('.chi-form').first(), {
    inject: ['components'],
    name: 'TabContent',
    parent: vm
  }, true);
  event.sender.tabGroup
    .children('[aria-controls="' + tab_content.attr('id') + '"]')
    .data('content-loaded', 'true');
  this.$nextTick(function () {post_load.call(this.$root.$options.api.vm)});
}

function on_window_activate(event) {
  var config = {
    data: {
      background: false,
      components: {},
      default_focus: true,
      hotkeys: new Hotkeys(),
      title: this.$root.$options.api.vm.title,
      'top-menu': {}
    },
    name: 'WindowContent',
    parent: this.$root.$options.api.vm,
    watch: {
      background: function (value) {
        $(this.$el).closest('.k-window').toggleClass('background', value);
      }
    }
  };
  config.destroyed = function () {
    console.debug('destroyed', this);
    Object.keys(this.components).forEach(function (safe_id) {
      var component = this.components[safe_id];
      if ('$destroy' in component)
        component.$destroy();
    }, this);
  };
  config.mounted = function () {
    var win = $(this.$el).closest('.k-window');
    console.debug('mounted', this, win);
    event.sender.center();
    wrap_window_events(win, [
      win, $(this.$el).closest('.k-window-content'),
      win.children('.k-resize-handle'), win.children('.k-window-titlebar')
    ]);
    // remove menu buttons if needed
    var titlebar = win.find('.k-window-titlebar');
    titlebar.addClass('no-jump-out no-menu-items');
    if (!win.find('.k-window-content>form>.with-top-menu').length)
      titlebar.addClass('no-top-menu');
    // update internal properties
    this.$root.context_menu_data = [];
    this.$root.$options.api.vm = this;
    this.$root.$options.api.vm_id = this._uid;
    this.$root.form_operations.operation_complete();
    this.$nextTick(post_load.bind(this, true));
    console.debug('show-form complete');
  };
  config.provide = function () {
    return {components: this.components};
  };
  activate_content.call(this, event.sender.element, config);
}

function on_window_close(event) {
  console.debug('on_window_close', event, this);
  if (event.userTriggered) {
    event.preventDefault();
    this.$options.socket.emit('close', []);
  }
}

function on_window_deactivate(event) {
  console.debug('on_window_deactivate', this, event);
}

function on_window_error(event) {
  console.debug('on_window_error', this, event);
  console.error('Failed to load window content: "%s"', event.status);
  this.$options.api.vm.ignore_next_hide_form = true;
  this.windows.pop();
  setImmediate(function (self) {
    var win = $(self.$options.api.vm.$el).closest('.k-window');
    win.removeClass('background');
    self.form_operations.operation_complete();
  }, this);
}

function on_window_open(event) {
  console.debug('open-window', this, event);
  var win = event.sender.wrapper;
  this.$root.windows.peek().kendo = event.sender;
  this.$nextTick(function () {
    var win_actions = win.find('.k-window-actions');
    win_actions
      .find('[aria-label="jump-out"]')
      .attr('title', 'Return to main menu');
    win_actions.on('click', window_actions_click.bind(this, win));
  }, this);
}

export default {
  activate_content: activate_content,
  on_tab_contentload: on_tab_contentload,
  on_window_activate: on_window_activate,
  on_window_close: on_window_close,
  on_window_deactivate: on_window_deactivate,
  on_window_error: on_window_error,
  on_window_open: on_window_open
};
